import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import "./profile-management.scss";
import ProfileHeader from "../../../../common/components/profile-header/profile-header-and-breadcrumbs";
import editIcon from "../../../../assets/icons/edit-outlined-icon.svg";
import securityIcon from "../../../../assets/icons/key-icon.svg";
import cameraIcon from "../../../../assets/icons/photo-icon.svg";
import arrowRightIcon from "../../../../assets/icons/right-grey-arrow.svg";
import clearIcon from "../../../../assets/icons/white-color-cancel-icon.svg";
import InfoPopup from "../../../../common/components/info-popup/info-popup";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ChangePasswordModal from "./change-password-modal";
import {
    getAdminProfile,
    updateAdminProfile,
    uploadAdminProfilePhoto,
    deleteAdminProfilePhoto,
} from "../../../../api/admin/admin-profile.api";
import { updateSessionDetails } from "../../../../common/routines/user-operations";

const ProfileManagement: React.FC = () => {
    const superAdminDetails = useSelector(
        (state: any) => state.session.superAdminDetails
    );
    const dispatch = useDispatch();

    const [isEditing, setIsEditing] = useState(false);
    const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
    const [showInfoPopup, setShowInfoPopup] = useState(false);
    const [infoPopupMessage, setInfoPopupMessage] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [errors, setErrors] = useState({
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
    });

    const [profileData, setProfileData] = useState({
        firstName: "",
        middleName: "",
        lastName: "",
        email: "",
        phone: "",
        countryCode: "+1",
        role: "",
        lastLogin: "",
        profilePic: null as string | null,
    });

    const [editData, setEditData] = useState({ ...profileData });

    const fetchAdminProfile = async () => {
        try {
            setIsLoading(true);
            const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
            const response = await getAdminProfile({ timezone });
            if (response.data?.data) {
                const data = response.data.data;
                const formatLastLogin = (dateString: string) => {
                    if (!dateString || dateString === "-") return "-";
                    const d = new Date(dateString);
                    if (isNaN(d.getTime())) return dateString;
                    const mm = String(d.getMonth() + 1).padStart(2, '0');
                    const dd = String(d.getDate()).padStart(2, '0');
                    const yyyy = d.getFullYear();
                    let hours = d.getHours();
                    const minutes = String(d.getMinutes()).padStart(2, '0');
                    const ampm = hours >= 12 ? 'PM' : 'AM';
                    hours = hours % 12;
                    hours = hours ? hours : 12;
                    const strTime = `${String(hours).padStart(2, '0')}:${minutes} ${ampm}`;
                    return `${mm}/${dd}/${yyyy} - ${strTime}`;
                };

                let mNumber = data.mobileNumber || "";
                let cCode = "+1";
                if (mNumber.startsWith("+91")) {
                    cCode = "+91";
                    mNumber = mNumber.slice(3);
                } else if (mNumber.startsWith("+1")) {
                    cCode = "+1";
                    mNumber = mNumber.slice(2);
                }

                const formattedData = {
                    firstName: data.firstName || "",
                    middleName: data.middleName || "",
                    lastName: data.lastName || "",
                    email: data.email || "",
                    phone: mNumber,
                    countryCode: cCode,
                    role: data.role === "Super Admin" ? "Admin" : (data.role || "Admin"),
                    lastLogin: formatLastLogin(data.lastLoginAt),
                    profilePic: data.profileImage || null,
                };
                setProfileData(formattedData);
                setEditData(formattedData);

                updateSessionDetails("superAdmin", {
                    first_name: data.firstName || "",
                    last_name: data.lastName || "",
                    profile_photo: data.profileImage || "",
                    profileImage: data.profileImage || "",
                    profilePhoto: data.profileImage || ""
                });
            }
        } catch (error) {
            console.error("Error fetching admin profile:", error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchAdminProfile();
    }, []);

    const handleEditClick = () => {
        setEditData({ ...profileData });
        setIsEditing(true);
    };

    const handleCancelEdit = () => {
        setIsEditing(false);
    };

    const handleSaveProfile = async () => {
        const newErrors = { firstName: "", lastName: "", email: "", phone: "" };
        let hasError = false;

        if (!editData.firstName) {
            newErrors.firstName = "First name is required";
            hasError = true;
        }
        if (!editData.lastName) {
            newErrors.lastName = "Last name is required";
            hasError = true;
        }
        if (!editData.email) {
            newErrors.email = "Email is required";
            hasError = true;
        } else {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(editData.email)) {
                newErrors.email = "Please enter a valid email address";
                hasError = true;
            }
        }
        if (!editData.phone) {
            newErrors.phone = "Phone number is required";
            hasError = true;
        } else if (editData.phone.length !== 10) {
            newErrors.phone = "Phone number must be exactly 10 digits";
            hasError = true;
        }

        setErrors(newErrors);
        if (hasError) return;

        // Check if any data has actually changed
        const isChanged =
            editData.firstName !== profileData.firstName ||
            editData.middleName !== profileData.middleName ||
            editData.lastName !== profileData.lastName ||
            editData.email !== profileData.email ||
            editData.phone !== profileData.phone ||
            editData.countryCode !== profileData.countryCode;

        if (!isChanged) {
            setIsEditing(false);
            return;
        }

        try {
            setIsLoading(true);
            const payload = {
                firstName: editData.firstName,
                middleName: editData.middleName,
                lastName: editData.lastName,
                email: editData.email,
                mobileNumber: `${editData.countryCode}${editData.phone}`,
            };
            const response = await updateAdminProfile(payload);
            setProfileData({ ...editData });
            setIsEditing(false);
            updateSessionDetails("superAdmin", {
                first_name: editData.firstName,
                last_name: editData.lastName,
            });
            fetchAdminProfile();
            const successMsg = response?.data?.data?.message || "Profile updated successfully.";
            setInfoPopupMessage(successMsg);
            setShowInfoPopup(true);
        } catch (error: any) {
            console.error("Error updating profile:", error);
            const errorData = error?.response?.data;
            if (errorData?.code === "VALIDATION_ERROR" && Array.isArray(errorData.errors)) {
                const apiErrors = { ...errors };
                let firstErrorMessage = errorData.message || "Input validation failed";
                errorData.errors.forEach((errObj: any) => {
                    if (errObj.firstName) apiErrors.firstName = errObj.firstName;
                    if (errObj.lastName) apiErrors.lastName = errObj.lastName;
                    if (errObj.middleName) {
                        firstErrorMessage = errObj.middleName;
                    }
                    if (errObj.email) apiErrors.email = errObj.email;
                    if (errObj.mobileNumber || errObj.phone) apiErrors.phone = errObj.mobileNumber || errObj.phone;
                    // For capturing the popup display message
                    const values = Object.values(errObj);
                    if (values.length > 0 && typeof values[0] === 'string') {
                        firstErrorMessage = values[0]; // Take the first detailed error
                    }
                });
                setErrors(apiErrors);
                setInfoPopupMessage(firstErrorMessage);
            } else {
                setInfoPopupMessage(errorData?.message || "Failed to update profile. Please try again.");
            }
            setShowInfoPopup(true);
        } finally {
            setIsLoading(false);
        }
    };

    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];
            const formData = new FormData();
            formData.append("profilePhoto", file);

            try {
                setIsLoading(true);
                const response = await uploadAdminProfilePhoto(formData);
                const newPhoto = response.data?.profileImage || response.data?.data?.profileImage || response.data?.profilePhoto || response.data?.data?.profile_photo;

                if (newPhoto) {
                    // Use functional updaters to preserve all other edited fields
                    setEditData((prev) => ({ ...prev, profilePic: newPhoto }));
                    setProfileData((prev) => ({ ...prev, profilePic: newPhoto }));
                    updateSessionDetails("superAdmin", {
                        profile_photo: newPhoto,
                        profileImage: newPhoto,
                        profilePhoto: newPhoto
                    });
                }
            } catch (error: any) {
                console.error("Error uploading photo:", error);
                setInfoPopupMessage(error?.response?.data?.message || "Failed to upload photo.");
                setShowInfoPopup(true);
            } finally {
                setIsLoading(false);
            }
        }
    };

    const handleDeletePhoto = async () => {
        try {
            setIsLoading(true);
            await deleteAdminProfilePhoto();
            // Use functional updaters to preserve all other edited fields
            setEditData((prev) => ({ ...prev, profilePic: null }));
            setProfileData((prev) => ({ ...prev, profilePic: null }));
            updateSessionDetails("superAdmin", {
                profile_photo: "",
                profileImage: "",
                profilePhoto: ""
            });
        } catch (error: any) {
            console.error("Error deleting photo:", error);
            setInfoPopupMessage(error?.response?.data?.message || "Failed to remove photo.");
            setShowInfoPopup(true);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="profile-management-container">
            <ProfileHeader
                breadcrumbLink={[
                    { label: "Profile Management" },
                ]}
            />

            <div className="profile-content-wrapper">
                {!isEditing ? (
                    <div className="profile-view">
                        <div className="profile-card">
                            <div className="profile-info-header">
                                <div className="profile-avatar-wrapper">
                                    {profileData.profilePic ? (
                                        <img src={profileData.profilePic} alt="Profile" />
                                    ) : (
                                        <div className="avatar-placeholder">
                                            <img src={cameraIcon} alt="Default Avatar" />
                                        </div>
                                    )}
                                </div>
                                <div className="profile-name-role">
                                    <h2>{`${profileData.firstName} ${profileData.lastName}`}</h2>
                                    <p>User Profile</p>
                                </div>
                            </div>
                            <button className="edit-profile-btn" onClick={handleEditClick}>
                                <img src={editIcon} alt="Edit" />
                                Edit Profile
                            </button>
                        </div>

                        <div className="info-section">
                            <h3>Basic Information</h3>
                            <div className="info-grid">
                                <div className="info-item">
                                    <label>First Name</label>
                                    <span>{profileData.firstName}</span>
                                </div>
                                <div className="info-item">
                                    <label>Middle Name</label>
                                    <span>{profileData.middleName || "-"}</span>
                                </div>
                                <div className="info-item">
                                    <label>Last Name</label>
                                    <span>{profileData.lastName}</span>
                                </div>
                                <div className="info-item">
                                    <label>Email Address</label>
                                    <span>{profileData.email}</span>
                                </div>
                                <div className="info-item">
                                    <label>Phone Number</label>
                                    <span>{profileData.phone ? `${profileData.countryCode}${profileData.phone}` : "-"}</span>
                                </div>
                                <div className="info-item">
                                    <label>Role</label>
                                    <span>{profileData.role}</span>
                                </div>
                                <div className="info-item">
                                    <label>Last Login Date & Time</label>
                                    <span>{profileData.lastLogin}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="edit-profile-form">
                        <h2>Edit Profile</h2>

                        <div className="photo-upload-section">
                            <label>Profile Photo (Optional)</label>
                            <div className="upload-controls">
                                <div className="avatar-preview">
                                    {editData.profilePic ? (
                                        <>
                                            <img src={editData.profilePic} alt="Preview" className="profile-img" />
                                            <button
                                                className="clear-photo-btn"
                                                onClick={handleDeletePhoto}
                                                disabled={isLoading}
                                                style={{
                                                    position: 'absolute',
                                                    top: '0',
                                                    right: '0',
                                                    backgroundColor: '#FF0000',
                                                    border: 'none',
                                                    borderRadius: '50%',
                                                    width: '20px',
                                                    height: '20px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    cursor: isLoading ? 'not-allowed' : 'pointer',
                                                    padding: '0',
                                                    zIndex: 10,
                                                    boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                                                }}
                                            >
                                                <img src={clearIcon} alt="Clear" style={{ width: '10px', height: '10px' }} />
                                            </button>
                                        </>
                                    ) : (
                                        <div className="avatar-placeholder">
                                            <img src={cameraIcon} alt="Default Avatar" />
                                        </div>
                                    )}
                                </div>
                                <label htmlFor="profile-upload" className={`upload-btn ${isLoading ? 'disabled' : ''}`} style={isLoading ? { opacity: 0.5, pointerEvents: 'none' } : {}}>
                                    Upload Photo
                                    <input
                                        id="profile-upload"
                                        type="file"
                                        hidden
                                        onChange={handleFileChange}
                                        accept="image/*"
                                        disabled={isLoading}
                                    />
                                </label>
                            </div>
                        </div>

                        <div className="form-grid">
                            <div className="form-group">
                                <label>First Name *</label>
                                <input
                                    type="text"
                                    value={editData.firstName}
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        if (/^[a-zA-Z\s]*$/.test(val) && val.length <= 30) {
                                            setEditData({ ...editData, firstName: val });
                                            if (errors.firstName && val) setErrors({ ...errors, firstName: "" });
                                        }
                                    }}
                                    className={errors.firstName ? "error-input" : ""}
                                />
                                {errors.firstName && <span className="error-message">{errors.firstName}</span>}
                            </div>
                            <div className="form-group">
                                <label>Middle Name</label>
                                <input
                                    type="text"
                                    value={editData.middleName}
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        if (/^[a-zA-Z\s]*$/.test(val) && val.length <= 30) {
                                            setEditData({ ...editData, middleName: val });
                                        }
                                    }}
                                />
                            </div>
                            <div className="form-group">
                                <label>Last Name *</label>
                                <input
                                    type="text"
                                    value={editData.lastName}
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        if (/^[a-zA-Z\s]*$/.test(val) && val.length <= 30) {
                                            setEditData({ ...editData, lastName: val });
                                            if (errors.lastName && val) setErrors({ ...errors, lastName: "" });
                                        }
                                    }}
                                    className={errors.lastName ? "error-input" : ""}
                                />
                                {errors.lastName && <span className="error-message">{errors.lastName}</span>}
                            </div>
                            <div className="form-group">
                                <label>Email Address *</label>
                                <input
                                    type="email"
                                    value={editData.email}
                                    readOnly
                                    className={errors.email ? "error-input" : ""}
                                    style={{ backgroundColor: "#f5f5f5", color: "#666", cursor: "not-allowed" }}
                                />
                                {errors.email && <span className="error-message">{errors.email}</span>}
                            </div>
                            <div className="form-group">
                                <label>Phone Number *</label>
                                <div style={{ display: 'flex', gap: '8px' }}>
                                    <select
                                        value={editData.countryCode}
                                        onChange={(e) => setEditData({ ...editData, countryCode: e.target.value })}
                                        style={{ width: '70px', padding: '12px 0px 12px 8px', border: '1px solid #e0e0e0', borderRadius: '8px', fontSize: '16px', backgroundColor: '#fff', cursor: 'pointer' }}
                                    >
                                        <option value="+1">+1</option>
                                        <option value="+91">+91</option>
                                    </select>
                                    <input
                                        type="text"
                                        value={editData.phone}
                                        onChange={(e) => {
                                            const val = e.target.value;
                                            if (/^\d{0,10}$/.test(val)) {
                                                setEditData({ ...editData, phone: val });
                                                if (errors.phone && val.length === 10) setErrors({ ...errors, phone: "" });
                                            }
                                        }}
                                        className={errors.phone ? "error-input" : ""}
                                        style={{ flexGrow: 1 }}
                                    />
                                </div>
                                {errors.phone && <span className="error-message">{errors.phone}</span>}
                            </div>
                            <div className="form-group">
                                <label>Role</label>
                                <input type="text" value={editData.role} disabled />
                            </div>
                            <div className="form-group">
                                <label>Last Login Date & Time</label>
                                <input type="text" value={editData.lastLogin} disabled />
                            </div>
                        </div>

                        <div className="form-actions">
                            <button className="cancel-btn" onClick={handleCancelEdit} disabled={isLoading}>Cancel</button>
                            <button className="save-btn" onClick={handleSaveProfile} disabled={isLoading}>{isLoading ? "Saving..." : "Save Changes"}</button>
                        </div>
                    </div>
                )}

                <div className="security-section">
                    <h3>Security</h3>
                    <p>Manage your password and security settings</p>
                    <div className="security-item" onClick={() => setIsPasswordModalOpen(true)}>
                        <div className="security-item-left">
                            <div className="security-icon">
                                <img src={securityIcon} alt="Security" />
                            </div>
                            <div className="security-text">
                                Change Password
                                <span>Update your account password</span>
                            </div>
                        </div>
                        <ChevronRightIcon sx={{ color: '#6A7282', fontSize: '22px' }} />
                    </div>
                </div>
            </div>

            <ChangePasswordModal
                open={isPasswordModalOpen}
                onClose={() => setIsPasswordModalOpen(false)}
                onSuccess={() => {
                    setIsPasswordModalOpen(false);
                    setInfoPopupMessage("Password updated successfully.");
                    setShowInfoPopup(true);
                }}
            />

            <InfoPopup
                open={showInfoPopup}
                note={infoPopupMessage}
                onClose={() => setShowInfoPopup(false)}
            />
        </div>
    );
};

export default ProfileManagement;
